export type URLLike = Location | URL | string;

const smartMark = (s: string) => (s.includes('=') && !s.includes('/') && !s.includes('?') && !s.includes('#') ? `?${s}` : s);

export const isURLLike = (url: any): url is URLLike => typeof url === 'string'
  || typeof url.href === 'string';

// 标准化 URL
export const toURL = (url: URLLike = '') => {
  const s = typeof url === 'string' ? smartMark(url) : url.href;
  return new URL(
    s.replace(/／/g, '/')
      .replace(/＆/g, '&')
      .replace(/＝/g, '='),
    location.href,
  );
};

/**
 * 获取指定名称的 url 参数
 * @param {String} name - 参数名称
 * @param {URLLike} [url=location.search] - 指定 url，不指定默认为当前页面 url
 * @returns {String} - 参数的值，未找到则返回 undefined
 */
export const getURLParam = (
  name: string,
  url: URLLike = location.search,
): string => parseSearch(url)[name];

// 解析 URL.search
export const parseSearch = (url?: URLLike) => Object.fromEntries(toURL(url).searchParams);

export interface SearchOptions {
  arrayFormat?: ArrayFormat
}

export const enum ArrayFormat {
  Default,  // a=1,2,3
  Indices,  // a[0]=1&a[1]=2&a[2]=3
  Brackets, // a[]=1&a[]=2&a[]=3
  Repeat,   // a=1&a=2&a=3
}

const arrayFormatFunc = {
  [ArrayFormat.Default]: (k: string, v: any[]) => [[k, v]] as [string, any][],
  [ArrayFormat.Indices]: (k: string, v: any[]) => v.map<[string, any]>(((o, i) => [`${k}[${i}]`, o])),
  [ArrayFormat.Brackets]: (k: string, v: any[]) => v.map<[string, any]>((o => [`${k}[]`, o])),
  [ArrayFormat.Repeat]: (k: string, v: any[]) => v.map<[string, any]>((o => [k, o])),
};

// 对象 url 转 a=1&b=2
export const newSearch = (params: { [k: string]: any }, { arrayFormat = ArrayFormat.Default }: SearchOptions = {}) => {
  const ps: [string, any][] = [];
  Object.keys(params).forEach((k) => {
    const v = params[k];
    ps.push(...(typeof v === 'object' ? arrayFormatFunc[arrayFormat](k, v) : ([[k, v]] as [string, any][])));
  });
  return new URLSearchParams(ps).toString();
};

export const searchBatchAppend = (paramsOrSearch: { [k: string]: any } | string = {}, url?: URLLike) => {
  const u = toURL(url);
  const ps = typeof paramsOrSearch === 'string' ? parseSearch(paramsOrSearch) : paramsOrSearch;
  // Object.entries(ps).forEach(([k, v]) => u.searchParams.append(k, v));
  // return u;

  // 覆盖旧的
  u.search = newSearch(Object.assign(parseSearch(u), ps));
  return u;
};

export const searchBatchDelete = (keys: string[] = [], url?: URLLike) => {
  const u = toURL(url);
  keys.forEach(k => u.searchParams.delete(k));
  return u;
};

export const parseQuery = parseSearch; // alias
export const newQuery = newSearch; // alias
export const queryBatchAppend = searchBatchAppend; // alias
export const queryBatchDelete = searchBatchDelete; // alias
